import { createSelector, createFeatureSelector } from '@ngrx/store';
import * as fromCommunications from './communications.reducer';
import { CommunicationsModuleState, communicationsModuleKey } from '../';

export interface CommunicationState {
  communications: fromCommunications.State;
}

const selectCommunicationsModuleState = createFeatureSelector<CommunicationsModuleState>(communicationsModuleKey);

const selectCommunicationsState = createSelector(
  selectCommunicationsModuleState,
  (state: CommunicationsModuleState) => state?.communications
);

//
// Organization Channels
//
export const selectOrganizationChannelQueryParams = createSelector(
  selectCommunicationsState,
  fromCommunications.getOrganizationChannelQueryParams
);
export const selectOrganizationChannels = createSelector(selectCommunicationsState, fromCommunications.getOrganizationChannels);

//
// Configured Channels
//
export const selectConfiguredChannels = createSelector(selectCommunicationsState, fromCommunications.getConfiguredChannels);
export const selectConfiguredChannelQueryParams = createSelector(
  selectCommunicationsState,
  fromCommunications.getConfiguredChannelQueryParams
);
export const selectTopoEmailEditIsActive = createSelector(selectCommunicationsState, fromCommunications.getTopoEmailEditIsActive);
export const selectSendGridEmailEditIsActive = createSelector(selectCommunicationsState, fromCommunications.getSendGridEmailEditIsActive);
export const selectEverbridgeEditIsActive = createSelector(selectCommunicationsState, fromCommunications.getEverbridgeEditIsActive);
export const selectSlackEditIsActive = createSelector(selectCommunicationsState, fromCommunications.getSlackEditIsActive);
export const selectMicrosoftTeamsEditIsActive = createSelector(selectCommunicationsState, fromCommunications.getMicrosoftTeamsEditIsActive);
export const selectTwilioEditIsActive = createSelector(selectCommunicationsState, fromCommunications.getTwilioEditIsActive);
export const selectTwilioTtsEditIsActive = createSelector(selectCommunicationsState, fromCommunications.getTwilioTtsEditIsActive);
export const selectWhatsAppEditIsActive = createSelector(selectCommunicationsState, fromCommunications.getWhatsAppEditIsActive);
export const selectHorizonSmsEditIsActive = createSelector(selectCommunicationsState, fromCommunications.getHorizonSmsEditIsActive);
export const selectHorizonMobilePushEditIsActive = createSelector(
  selectCommunicationsState,
  fromCommunications.getHorizonMobilePushEditIsActive
);
export const selectGwtsMobileEditIsActive = createSelector(
  selectCommunicationsState,
  fromCommunications.getGwtsMobileEditIsActive
);

export const selectSelectedConfiguredChannel = createSelector(selectCommunicationsState, fromCommunications.getSelectedConfiguredChannel);
export const selectSelectedConfiguredChannelToDelete = createSelector(
  selectCommunicationsState,
  fromCommunications.getSelectedConfiguredChannelToDelete
);
export const selectSlackChannelsForConfiguredChannel = createSelector(
  selectCommunicationsState,
  fromCommunications.getSlackChannelsForConfiguredChannel
);
export const selectIsLoading = createSelector(selectCommunicationsState, fromCommunications.getIsLoading);
export const selectDefaultConfiguredChannel = createSelector(
  selectCommunicationsState,
  state => state.defaultConfiguredChannelSettings || null
);

//
// Distribution Lists
//
export const selectDistributionListsQueryParams = createSelector(
  selectCommunicationsState,
  fromCommunications.getDistributionListsQueryParams
);
export const selectDistributionLists = createSelector(selectCommunicationsState, fromCommunications.getDistributionLists);
export const selectDistributionListNewIsActive = createSelector(
  selectCommunicationsState,
  fromCommunications.getDistributionListNewIsActive
);
export const selectSelectedDistributionList = createSelector(selectCommunicationsState, fromCommunications.getSelectedDistributionList);
export const selectSelectedDistributionListToDelete = createSelector(
  selectCommunicationsState,
  fromCommunications.getSelectedDistributionListToDelete
);
export const selectDistributionListDataQueryParams = createSelector(
  selectCommunicationsState,
  fromCommunications.getDistributionListDataQueryParams
);
export const selectDistributionListDataDocuments = createSelector(
  selectCommunicationsState,
  fromCommunications.getDistributionListDataDocuments
);

//
// Templates
//
export const selectTemplateQueryParams = createSelector(selectCommunicationsState, fromCommunications.getTemplateQueryParams);
export const selectTemplates = createSelector(selectCommunicationsState, fromCommunications.getTemplates);
export const selectTemplateNewIsActive = createSelector(selectCommunicationsState, fromCommunications.getTemplateNewIsActive);
export const selectSelectedTemplate = createSelector(selectCommunicationsState, fromCommunications.getSelectedTemplate);
export const selectSelectedTemplateToDelete = createSelector(selectCommunicationsState, fromCommunications.getSelectedTemplateToDelete);
export const selectTemplateCopyIsActive = createSelector(selectCommunicationsState, fromCommunications.getTemplateCopyIsActive);
export const selectSelectedTemplateToCopy = createSelector(selectCommunicationsState, fromCommunications.getSelectedTemplateToCopy);
export const selectEverbridgeTemplates = createSelector(selectCommunicationsState, fromCommunications.getEverbridgeTemplates);

//
// Mass Notification Templates
//
export const selectSelectedMassNotificationTemplate = createSelector(
  selectCommunicationsState,
  fromCommunications.getSelectedMassNotificationTemplate
);

export const selectIsdMassNotificationTemplatesTableChanged = createSelector(
  selectCommunicationsState,
  fromCommunications.getMassNotificationTemplatesTableChanged
);

export const selectIsdMassNotificationTemplatesDirty = createSelector(
  selectCommunicationsState,
  fromCommunications.getMassNotificationTemplatesDirty
);
export const selectWhatsAppTemplates = createSelector(selectCommunicationsState, fromCommunications.getWhatsAppTemplates);

//
// Mass Notification Template Groups
//
export const selectSelectedMassNotificationTemplateGroup = createSelector(
  selectCommunicationsState,
  fromCommunications.getSelectedMassNotificationTemplateGroup
);

export const selectIsMassNotificationTemplateGroupsTableChanged = createSelector(
  selectCommunicationsState,
  fromCommunications.getMassNotificationTemplateGroupsTableChanged
);

export const selectIsMassNotificationTemplateGroupsDirty = createSelector(
  selectCommunicationsState,
  fromCommunications.getMassNotificationTemplateGroupsDirty
);

//
// Mass Notifications
//
export const selectSelectedMassNotification = createSelector(selectCommunicationsState, fromCommunications.getSelectedMassNotification);

export const selectIsdMassNotificationsTableChanged = createSelector(
  selectCommunicationsState,
  fromCommunications.getMassNotificationsTableChanged
);

export const selectIsdMassNotificationDirty = createSelector(selectCommunicationsState, fromCommunications.getMassNotificationDirty);

export const selectIsMassNotificationEditing = createSelector(selectCommunicationsState, fromCommunications.getMassNotificationEditing);

export const selectSelectedMassNotificationRecipient = createSelector(
  selectCommunicationsState,
  fromCommunications.getSelectedMassNotificationRecipient
);

export const selectIsdMassNotificationRecipientsTableChanged = createSelector(
  selectCommunicationsState,
  fromCommunications.getMassNotificationRecipientsTableChanged
);
export const selectSelectedMassNotificationStats = createSelector(
  selectCommunicationsState,
  fromCommunications.getSelectedMassNotificationStats
);

//
// Datasets
//
export const selectCommunicationCenterDataset = createSelector(selectCommunicationsState, fromCommunications.getCommunicationCenterDataset);
export const selectDatasetsForSelect = createSelector(selectCommunicationsState, fromCommunications.getDatasetsForSelect);
